"use client";

import { useEffect } from "react";

type ClientErrorPayload = {
  source: "window.error" | "unhandledrejection";
  message: string;
  stack: string | null;
  href: string;
  userAgent: string;
  timestamp: string;
};

function normalizeMessage(value: unknown) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (value instanceof Error) {
    return value.message?.trim() || value.name || null;
  }

  if (typeof value === "object" && value) {
    const record = value as { message?: unknown; name?: unknown };
    if (typeof record.message === "string" && record.message.trim()) {
      return record.message.trim();
    }
    if (typeof record.name === "string" && record.name.trim()) {
      return record.name.trim();
    }
  }

  if (value == null) return null;

  const fallback = String(value).trim();
  if (!fallback || fallback === "[object Object]") return null;
  return fallback;
}

function normalizeStack(value: unknown) {
  if (value instanceof Error) return value.stack ?? null;
  if (typeof value === "object" && value) {
    const record = value as { stack?: unknown };
    return typeof record.stack === "string" && record.stack.trim() ? record.stack : null;
  }
  return null;
}

function isActionablePayload(payload: ClientErrorPayload) {
  const message = payload.message.trim();
  if (!message) return false;
  if (message === "Unknown error") return false;
  if (message === "[object Object]") return false;
  return Boolean(message || payload.stack?.trim());
}

export function DevRuntimeErrorLogger() {
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;

    let hasWarnedAboutEndpoint = false;
    const seenPayloadKeys = new Set<string>();

    const sendPayload = (payload: ClientErrorPayload) => {
      if (!isActionablePayload(payload)) {
        return;
      }

      const payloadKey = [
        payload.source,
        payload.message,
        payload.stack ?? "",
        payload.href,
      ].join("::");

      if (seenPayloadKeys.has(payloadKey)) {
        return;
      }
      seenPayloadKeys.add(payloadKey);

      console.warn("[dev-runtime-error]", payload);

      try {
        void fetch("/api/_debug/client-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        })
          .then((response) => {
            if (!response.ok && !hasWarnedAboutEndpoint) {
              hasWarnedAboutEndpoint = true;
              console.warn("[dev-runtime-error] failed to POST client error", {
                status: response.status,
                href: payload.href,
              });
            }
          })
          .catch((error) => {
            if (!hasWarnedAboutEndpoint) {
              hasWarnedAboutEndpoint = true;
              console.warn("[dev-runtime-error] network failure while reporting client error", error);
            }
          });
      } catch {
        // Never throw from dev diagnostics.
      }
    };

    const onError = (event: ErrorEvent) => {
      try {
        const message = normalizeMessage(event.error ?? event.message) ?? "Unknown error";
        sendPayload({
          source: "window.error",
          message,
          stack: normalizeStack(event.error),
          href: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });
      } catch {
        // Never throw from dev diagnostics.
      }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      try {
        const message = normalizeMessage(event.reason) ?? "Unknown error";
        sendPayload({
          source: "unhandledrejection",
          message,
          stack: normalizeStack(event.reason),
          href: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        });
      } catch {
        // Never throw from dev diagnostics.
      }
    };

    try {
      window.addEventListener("error", onError);
      window.addEventListener("unhandledrejection", onRejection);
      return () => {
        try {
          window.removeEventListener("error", onError);
          window.removeEventListener("unhandledrejection", onRejection);
        } catch {
          // Never throw from dev diagnostics.
        }
      };
    } catch {
      // Never throw from dev diagnostics.
      return;
    }
  }, []);

  return null;
}

export default DevRuntimeErrorLogger;
